import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const PAD = 8;

export const GuidedTour = ({ steps, flag, testPrefix = "tour" }) => {
  const { user, setUser } = useAuth();
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const active = !!user && !user[flag] && !dismissed && steps.length > 0;
  const step = steps[Math.min(idx, steps.length - 1)];

  const measure = useCallback(() => {
    if (!step?.target) {
      setRect(null);
      return;
    }
    const el = document.querySelector(step.target);
    if (!el) {
      setRect(null);
      return;
    }
    el.scrollIntoView({ block: "center", behavior: "instant" });
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [step]);

  useEffect(() => {
    if (!active) return;
    const t = setTimeout(measure, 150);
    window.addEventListener("resize", measure);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", measure);
    };
  }, [active, measure]);

  if (!active) return null;

  const complete = async () => {
    setDismissed(true);
    try {
      const { data } = await api.put("/profile", { [flag]: true });
      setUser(data);
    } catch {
      toast.error("No se pudo guardar el progreso del tour");
    }
  };

  const last = idx === steps.length - 1;

  // tooltip position: below the target, clamped; centered when no target
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const tipW = Math.min(340, vw - 24);
  let tipStyle;
  if (rect) {
    const below = rect.top + rect.height + PAD + 190 < vh;
    tipStyle = {
      top: below ? rect.top + rect.height + PAD + 4 : Math.max(12, rect.top - PAD - 200),
      left: Math.min(Math.max(12, rect.left + rect.width / 2 - tipW / 2), vw - tipW - 12),
      width: tipW,
    };
  } else {
    tipStyle = { top: vh / 2 - 120, left: vw / 2 - tipW / 2, width: tipW };
  }

  return createPortal(
    <div className="fixed inset-0 z-[100]" data-testid={`${testPrefix}-overlay`}>
      {rect ? (
        <div
          className="fixed rounded-2xl transition-all duration-300 pointer-events-none"
          style={{
            top: rect.top - PAD, left: rect.left - PAD,
            width: rect.width + PAD * 2, height: rect.height + PAD * 2,
            boxShadow: "0 0 0 9999px rgba(23,25,39,0.62)",
            border: "2px solid rgba(255,255,255,0.6)",
          }}
        />
      ) : (
        <div className="fixed inset-0 bg-[#171927]/62" />
      )}
      <div className="fixed rounded-3xl bg-card shadow-2xl p-5 space-y-3 animate-fade-up" style={tipStyle} data-testid={`${testPrefix}-tooltip`}>
        <div className="flex items-start gap-3">
          <span className="text-2xl" aria-hidden>{step.emoji}</span>
          <div>
            <p className="font-heading font-bold" data-testid={`${testPrefix}-title`}>{step.title}</p>
            <p className="text-sm text-muted-foreground mt-1">{step.text}</p>
          </div>
        </div>
        <div className="flex items-center justify-between pt-1">
          <div className="flex items-center gap-1.5">
            {steps.length > 1 && steps.map((_, i) => (
              <span key={i} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-4 bg-primary" : "w-1.5 bg-muted-foreground/30"}`} />
            ))}
          </div>
          <div className="flex gap-1.5">
            {!last && (
              <Button variant="ghost" size="sm" onClick={complete} data-testid={`${testPrefix}-skip-btn`}>
                Saltar
              </Button>
            )}
            {idx > 0 && (
              <Button variant="outline" size="sm" onClick={() => setIdx(idx - 1)} data-testid={`${testPrefix}-back-btn`}>
                Atrás
              </Button>
            )}
            <Button size="sm" onClick={last ? complete : () => setIdx(idx + 1)} data-testid={`${testPrefix}-next-btn`}>
              {last ? "¡A por ello!" : "Siguiente"}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
